// 剣舞の技の音 v2 (2026-11-04・11-04 夕に作者が配布版へ採用・記録 = docs/research/2026-11-04-blade-sfx.md)。
//   作者の答え: 普段の振り・突きと回り・重い一撃と裾と帯・拍手2つ = v2、暗器 (予告と飛ぶ音) = v1 のまま。
//   前の版 (10-31 の v1 と借り物の拍手) は DEV ?bladeSfx=v1 で鳴る。v1 は前の4技の一戦向けで、今の一戦 (src/blade_dancer_arle.ts) では
//   旋回突きの5発が全部「突き」、引き寄せ・三連は共通の斬りに落ちる。拍手は2つとも会話場面の拍手の借り物 (adv_blade_clap)。
//   v2 は介錯 e (11-03) と同じく Surge XT で作り直し、技の発ごとに得物 (細い双剣・暗器) と体 (裾・帯の布) の音を当てる。
//   素材 = scripts/render_blade_sfx_v2.py → public/sfx/blade_sfx_v2_<役>.wav (preloadSfxSamples が読む・暗器の2本は鳴らさないので読まない)。
//   名前が blade_sfx_v2_ で始まる = ボスの技の音 (src/sound_priority.ts)。予告 (_tell_)・弾かれ (_parried) は下げない。
import type { SfxId } from './types'

export const BLADE_SFX_V2_ROLES = [
  'tell_knives', 'tell_clap', 'swing_s', 'swing_l', 'thrust', 'draw', 'spin', 'hem', 'ribbon',
  'crimson', 'burst', 'descent', 'knife', 'parried', 'exec', 'clap_self',
] as const
export type BladeSfxV2Role = typeof BLADE_SFX_V2_ROLES[number]
export const BLADE_SFX_V2_IDS = BLADE_SFX_V2_ROLES.map(r => `blade_sfx_v2_${r}` as const)

const BLADE = 'blade-dancer'
const ADV_CLAP: SfxId = 'adv_blade_clap'
let on = true
/** 剣舞で v2 を鳴らすか。 */
export function bladeSfxV2On(bossId: string): boolean { return on && bossId === BLADE }
export function setBladeSfx(value: string | null | undefined): 'v1' | 'shipped' {
  on = value !== 'v1'
  return on ? 'shipped' : 'v1'
}
const id = (r: BladeSfxV2Role): SfxId => `blade_sfx_v2_${r}`
// 暗器は v1 の音のまま (11-04 作者 q4=v1)。v2 の tell_knives・knife の素材は残すが鳴らさない。
const KNIFE_TELL_V1: SfxId = 'bsfx_v1_blade_tell_knives'
const KNIFE_V1: SfxId = 'bsfx_v1_blade_knife'
const UNUSED: readonly BladeSfxV2Role[] = ['tell_knives', 'knife']

// 旋回突きの5発 = 斬り上げ → 前へ払う → 回って大薙ぎ → 突き → 引き戻し (src/blade_dancer_arle.ts)。
const PIROUETTE: readonly BladeSfxV2Role[] = ['swing_s', 'swing_s', 'spin', 'thrust', 'draw']

/** 暗器を抜く予告 (溜めに入った tick)。体だけの予告は鳴らさない。 */
export function bladeSfxV2Tell(attackId: string): SfxId | null {
  return attackId === 'bladeKnifeThrow' ? KNIFE_TELL_V1 : null
}

/** 当たりに入った時 (連撃は1発ごと) の音。 */
export function bladeSfxV2Active(attackId: string, hitIndex: number, hits: number): SfxId {
  const last = hitIndex >= hits - 1
  switch (attackId) {
    case 'dancerExecution': return id('exec')
    case 'hemSweep': return id('hem')
    case 'bladeKnifeThrow': return KNIFE_V1
    case 'pirouette': return id(PIROUETTE[Math.min(hitIndex, PIROUETTE.length - 1)]!)
    case 'bladeCrimson': return id(hitIndex === 0 ? 'crimson' : 'burst')       // γ: 重い斬り → 剣先の床が爆ぜる
    case 'bladeDescent': return id(hitIndex === 0 ? 'descent' : 'swing_l')     // δ: 叩きつけ → 払い
    case 'bladeRibbon': return id(hitIndex === 0 ? 'ribbon' : 'swing_l')       // 引き寄せ: 帯を投げる → 足もとを払う
    case 'bladeSpinChain': return id(last ? 'swing_l' : 'spin')
    default: return id(last && hits > 1 ? 'swing_l' : 'swing_s')                // 三連・大振り・終舞・速い連 (速い連は答え待ち)
  }
}

export function bladeSfxV2Parried(): SfxId { return id('parried') }

/** 自分で打つ拍手 (連を弾き切られた後・2拍で1本)。 */
export function bladeSelfClapSfx(): SfxId { return on ? id('clap_self') : ADV_CLAP }
/** とどめの手拍子 (1拍ずつ)。 */
export function bladeFinisherClapSfx(): SfxId { return on ? id('tell_clap') : ADV_CLAP }

/** 先読みする素材 [キー, URL] (鳴らす役だけ)。 */
export function bladeSfxV2Files(): [string, string][] {
  return BLADE_SFX_V2_ROLES.filter(r => !UNUSED.includes(r)).map(r => [id(r), `/sfx/${id(r)}.wav?v=1`])
}
